import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGoalTree,
  createTask,
  updateGoal,
  type Task,
  type GoalTreeNode,
  type DayHabit,
} from '../api';
import { computeGoalFocus, type FocusGoal } from '../goalFocus';
import { showAllGoalsLabel } from '../goalVocab';
import { QueryState } from './QueryState';

/**
 * Which of your goals today's plan moves, and which it does not.
 *
 * Goals lived on their own screen. Every task has carried `goalIds` since Phase 1 and no list
 * ever showed one, so the day view could not tell you that a full day of planned work served
 * nothing you had said you were trying to do — and the goals screen could not tell you its 40%
 * had no time booked against it.
 *
 * Nobody abandons a long goal deliberately. Days fill with work belonging to something else and
 * there is no moment where the two views are put side by side. This is that moment, and it is on
 * the day screen rather than the goals screen because that is where the decision is made.
 */

/** How many goals may ask you for a decision at once. Eleven of them is wallpaper. */
const DECISIONS_SHOWN = 2;

type Walked = FocusGoal & {
  habitIds: string[];
  /** How deep in the tree, so the pane can draw what the Goals screen draws. */
  depth: number;
  /** Every goal beneath this one, so a child's work can be rolled up to it. */
  descendantIds: string[];
  hasChildren: boolean;
};

/**
 * Walks the tree, keeping the shape.
 *
 * This was `flatten`, and its comment said a sub-goal deserves the same attention as a root one —
 * which is true and is not an argument for printing roots beside their own grandchildren in one
 * unindented column. Depth is kept now, and so is the set of descendants, because a branch node
 * structurally cannot hold tasks: eleven of fifteen rows read "nothing planned" permanently, and
 * the flattening is what manufactured that noise.
 */
function walk(nodes: readonly GoalTreeNode[], depth = 0): Walked[] {
  return nodes.flatMap((n) => {
    const below = walk(n.children, depth + 1);
    return [
      {
        id: n.goal.id,
        title: n.goal.title,
        paceState: n.pace.state,
        needsNextAction: n.pace.needsNextAction,
        nextActionState: n.pace.nextActionState,
        live: n.goal.live,
        percent: n.progress.percent,
        habitIds: n.habits.map((h) => h.id),
        depth,
        descendantIds: below.map((d) => d.id),
        hasChildren: n.children.length > 0,
      },
      ...below,
    ];
  });
}

/**
 * Adding work for a goal without leaving the day.
 *
 * The panel above says a goal has nothing planned; a warning whose remedy is three screens away
 * is a nag. Attaching a task to a goal otherwise means opening the task, finding a checkbox list
 * and ticking one — fine once, too much friction to do while planning a morning.
 *
 * The task lands undated and `next`, which puts it straight on the shortlist beside everything
 * else waiting to be dragged onto the day.
 */
function AddForGoal({ goalId, onDone }: { goalId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');

  const add = useMutation({
    mutationFn: (submitted: string) =>
      createTask({ title: submitted, status: 'next', goalIds: [goalId] }),
    // Same guard as the capture box: clear only what was actually sent, never newer typing.
    onSuccess: (_created, submitted) => {
      setTitle((current) => (current === submitted ? '' : current));
      onDone();
      void queryClient.invalidateQueries();
    },
  });

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) add.mutate(title.trim());
      }}
    >
      <input
        autoFocus
        value={title}
        placeholder="Something that moves this goal"
        aria-label="New task for this goal"
        onChange={(e) => setTitle(e.target.value)}
      />
      <button type="submit" disabled={!title.trim() || add.isPending}>
        Add
      </button>
    </form>
  );
}

const PACE_TONE: Record<string, string> = {
  overdue: 'bad',
  behind: 'warn',
  on_track: 'ok',
  ahead: 'ok',
  done: 'ok',
  no_target: '',
};

export function GoalsToday({
  scheduledTasks,
  candidates,
  habits,
  onOpenTask,
}: {
  /** Tasks with a block on the day being viewed. */
  scheduledTasks: Task[];
  candidates: Task[];
  /** The day's habits, so one logged today counts as serving its goal. */
  habits: DayHabit[];
  onOpenTask: (id: string) => void;
}) {
  // Shared cache key with the goals screen and the template form, so this adds no fetch in
  // practice.
  const tree = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const queryClient = useQueryClient();
  /* The same call the Goals screen makes, so "done" means one thing in both places. */
  const markDone = useMutation({
    mutationFn: (goalId: string) => updateGoal(goalId, { status: 'done' }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const loggedToday = useMemo(
    () => new Set(habits.filter((h) => h.loggedOnDay > 0).map((h) => h.id)),
    [habits],
  );

  const focus = useMemo(() => {
    if (!tree.data) return null;
    const walked = walk(tree.data);
    const goals = walked.filter(
      /*
        Paused, archived and finished goals are excluded, for the same reason pace ignores them:
        a pause is a decision, and nagging about it argues with the user.

        This comment has been here since the pane was written and the code beneath it checked
        `paceState !== 'done'` — which a paused goal passes, because pause makes its pace
        `no_target`. So a persona who deliberately paused their guitar goal kept being shown it
        with "nothing planned". Checking the status is what the comment always said.

        The check itself now comes off the payload (`goal.live`) rather than being spelled out
        again here — the server settles what "still being pursued" means, for this pane, the
        shortlist and the goal's own page alike.
      */
      (g) => g.live,
    );
    /*
      A habit logged today serves its goal as surely as a task does — for a goal like "run a
      Spartan trifecta" it is the *only* way it gets served — so it is fed in as a scheduled
      item. Without this the panel would tell someone who ran this morning that their running
      goal had nothing planned.
    */
    const habitItems = habits
      .filter((h) => loggedToday.has(h.id))
      .map((h) => ({
        id: `habit:${h.id}`,
        title: h.name,
        goalIds: goals.filter((g) => g.habitIds.includes(h.id)).map((g) => g.id),
      }))
      .filter((item) => item.goalIds.length > 0);

    const computed = computeGoalFocus({
      goals,
      scheduledTasks: [
        ...scheduledTasks.map((t) => ({ id: t.id, title: t.title, goalIds: t.goalIds })),
        ...habitItems,
      ],
      candidates: candidates.map((t) => ({ id: t.id, title: t.title, goalIds: t.goalIds })),
    });

    const shape = new Map(walked.map((w) => [w.id, w]));
    const scheduledCount = new Map(computed.goals.map((e) => [e.goal.id, e.scheduled.length]));

    /*
      A branch counts what its children are doing.

      A goal with sub-goals structurally cannot hold tasks, so it read "nothing planned" every day
      of its life while the work happened one level down. Rolling the descendants' count up means
      the row says something true — and means a branch only appears at all when there is something
      under it worth seeing.
    */
    const rolled = computed.goals.map((entry) => {
      const w = shape.get(entry.goal.id);
      const below = (w?.descendantIds ?? []).reduce(
        (n, id) => n + (scheduledCount.get(id) ?? 0),
        0,
      );
      return { ...entry, depth: w?.depth ?? 0, hasChildren: w?.hasChildren ?? false, below };
    });

    return { ...computed, goals: rolled };
  }, [tree.data, scheduledTasks, candidates, habits, loggedToday]);

  /*
    A failed read is not "no goals worth showing".

    This pane returns null when there is nothing to say, which is right — and it returned null on
    failure too, so a broken goals request looked exactly like an account with no active goals.
    On the one screen the product lives or dies by, that is the wrong silence.
  */
  if (tree.isError) return <QueryState query={tree} what="your goals" />;
  if (!focus || focus.goals.length === 0) return null;

  /*
    What actually goes on the screen you open every morning.

    All fifteen goals were printed, roots beside their own grandchildren, and eleven of the rows
    said "nothing planned" permanently because branch nodes cannot hold tasks. The planner account
    read "nothing planned" eight times before breakfast.

    Three rules, in this order:

    - **A goal that is finished leads.** The screen offered that goal "add something" while the
      Goals screen offered it "Mark it done" — so the one screen opened daily hid the win and
      asked for more work.
    - **`computeGoalFocus`'s ranking is otherwise kept exactly.** It already puts an ignored goal
      ahead of one being worked on, which is the entire purpose of the pane and is covered by a
      spec that says so. This caps the list; it does not re-rank it.
    - **At most two rows may say "nothing planned".** A decision you are asked to make twice is a
      decision; asked eleven times it is wallpaper. The cap is on those rows alone, so nothing
      that *is* happening today is ever dropped to make room.
  */
  const finishable = focus.goals.filter((g) => g.goal.nextActionState === 'ready_to_finish');
  let decisions = 0;
  const rest = focus.goals.filter((g) => {
    if (g.goal.nextActionState === 'ready_to_finish') return false;
    if (g.scheduled.length + g.below > 0) return true;
    decisions += 1;
    return decisions <= DECISIONS_SHOWN;
  });
  const shown = showAll ? focus.goals : [...finishable, ...rest];
  const hidden = focus.goals.length - shown.length;

  return (
    <section className="card goals-today" data-testid="goals-today">
      <h3>Goals today</h3>

      <ul className="plain goal-focus-list">
        {shown.map((entry) => (
          <li
            key={entry.goal.id}
            data-testid="goal-focus"
            data-goal-title={entry.goal.title}
            data-depth={entry.depth}
            /* The same indent the Goals screen uses, so the hierarchy the reader built is the
               hierarchy they see here. */
            style={{ marginLeft: `${entry.depth * 1.1}rem` }}
          >
            <div className="row gap">
              <span className="goal-focus-title">{entry.goal.title}</span>
              {entry.goal.paceState !== 'no_target' && (
                <span className={`pill ${PACE_TONE[entry.goal.paceState] ?? ''}`}>
                  {entry.goal.paceState.replace('_', ' ')}
                </span>
              )}
              {entry.goal.nextActionState === 'ready_to_finish' ? (
                /*
                  "nothing planned" beside "every task under this goal is done" is the row
                  contradicting itself. A finished goal has nothing planned *because* it is
                  finished, which is the opposite of the warning that pill exists to give.
                */
                <span className="pill ok" data-testid="goal-ready">
                  ready to finish
                </span>
              ) : entry.scheduled.length > 0 ? (
                <span className="pill ok" data-testid="goal-served">
                  {entry.scheduled.length} on the day
                </span>
              ) : entry.below > 0 ? (
                /* A branch holds no tasks itself. Saying "nothing planned" over work happening
                   one level down was the pane's most repeated untruth. */
                <span className="pill ok" data-testid="goal-served-below">
                  {entry.below} on the day, below
                </span>
              ) : entry.available.length > 0 ? (
                /*
                  On the shortlist but not on the day — the third state, which the chip used to fold
                  into the second.

                  "nothing planned" is rendered from `scheduled.length === 0`, which means *not on
                  the timeline*, and it says *nothing planned*, which is an accusation. Six goals
                  wore it in the 1.39.0 persona round on the line directly above their own "On the
                  shortlist: Travel insurance, Buy the rail pass, Check the passport expiry" — one
                  word, two predicates, and the planner persona is the one most sensitive to being
                  told it has not planned.

                  The neutral `pill`, not the `ok` one: having work ready and not having dated it is
                  neither a success to congratulate nor a failure to warn about. It is a fact, and
                  the line beneath it is the remedy.
                */
                <span className="pill" data-testid="goal-shortlisted">
                  {entry.available.length} on the shortlist
                </span>
              ) : (
                /* Nothing on the day, nothing below, nothing on the shortlist. The one reading
                   under which "nothing planned" is true, and the state the pane's own "Nothing
                   available for today" line describes. */
                <span className="pill" data-testid="goal-untouched">
                  nothing planned
                </span>
              )}
            </div>

            {entry.scheduled.length > 0 && (
              <p className="subtext">
                {entry.scheduled.map((t, i) => (
                  <span key={t.id}>
                    {i > 0 && ', '}
                    {t.id.startsWith('habit:') ? (
                      // A habit is not a task and has no detail panel to open.
                      <span title="Logged today">{t.title} ✓</span>
                    ) : (
                      <button className="link" onClick={() => onOpenTask(t.id)}>
                        {t.title}
                      </button>
                    )}
                  </span>
                ))}
              </p>
            )}

            {/* The remedy where the problem is stated: an untouched goal whose work is already
                on the shortlist only needs it dragging onto the day, so name it.

                "On the shortlist", not "Waiting on the shortlist". This line is a third sense of
                a word the app was spending three ways — a blocker (`BlockedBanner`), a promise
                from outside the account (`WaitingOn.tsx`), and this, which is neither. It is not
                "Blocked by" either: nothing is holding this work up, it is sitting somewhere the
                reader can pick it from. So it names the place instead of inventing a verb. */}
            {entry.scheduled.length === 0 && entry.available.length > 0 && (
              <p className="subtext">
                On the shortlist:{' '}
                {entry.available.slice(0, 3).map((t, i) => (
                  <span key={t.id}>
                    {i > 0 && ', '}
                    <button className="link" onClick={() => onOpenTask(t.id)}>
                      {t.title}
                    </button>
                  </span>
                ))}
              </p>
            )}

            {/*
              And a branch does not ask for work it cannot hold.

              "1 on the day, below" followed by "Nothing available for today, add something" is
              the same row disagreeing with itself one line later. A goal with sub-goals cannot
              carry tasks; the work is happening where it belongs.
            */}
            {entry.scheduled.length === 0 && entry.available.length === 0 && entry.below === 0 && (
              <p className="subtext">
                {/* Finished and never-started both have nothing open, and the difference is
                    the whole message. */}
                {entry.goal.nextActionState === 'ready_to_finish'
                  ? 'Every task under this goal is done — it is ready to finish.'
                  : entry.goal.needsNextAction
                    ? 'Nothing open on this goal at all — it needs a next step.'
                    : 'Nothing available for today.'}{' '}
                {/*
                  The win, offered here too.

                  This said "ready to finish" and then offered only "add something", while the
                  Goals screen gave the same goal a "Mark it done" button. The screen opened every
                  morning hid the finish and asked for more work.

                  Still only offered: finishing the list and finishing the goal are not the same
                  claim, which is exactly why the app must not make it for you.
                */}
                {entry.goal.nextActionState === 'ready_to_finish' && (
                  <>
                    <button
                      type="button"
                      data-testid="mark-goal-done"
                      disabled={markDone.isPending}
                      onClick={() => markDone.mutate(entry.goal.id)}
                    >
                      Mark it done
                    </button>{' '}
                  </>
                )}
                <button
                  className="link"
                  onClick={() => setAddingFor(addingFor === entry.goal.id ? null : entry.goal.id)}
                >
                  {addingFor === entry.goal.id
                    ? 'cancel'
                    : entry.goal.nextActionState === 'ready_to_finish'
                      ? 'or add more'
                      : 'add something'}
                </button>
              </p>
            )}

            {addingFor === entry.goal.id && (
              <AddForGoal goalId={entry.goal.id} onDone={() => setAddingFor(null)} />
            )}
          </li>
        ))}
      </ul>

      {/*
        Counted, so nobody wonders what is missing. The pane shows what needs a decision today,
        not everything you have ever wanted to do.

        And counted out loud: this said "Show all 8 goals" over the live goals while the
        Reflection said "Show all 9 goals" over the same account, because the filter above drops
        the one that is paused. Both true, one word — see `showAllGoalsLabel`.
      */}
      {hidden > 0 && !showAll && (
        <button className="link" onClick={() => setShowAll(true)} data-testid="goals-today-more">
          {showAllGoalsLabel(focus.goals.length, 'active')}
        </button>
      )}
      {showAll && (
        <button className="link" onClick={() => setShowAll(false)}>
          Show less
        </button>
      )}

      {focus.unaligned.length > 0 && (
        <p className="subtext" data-testid="unaligned-note">
          {focus.unaligned.length} planned task{focus.unaligned.length === 1 ? '' : 's'} not
          attached to any goal. That is fine for errands — worth noticing if it is the whole day.
        </p>
      )}
    </section>
  );
}
